import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Lock, ArrowLeft, ArrowRight, CheckCircle2, Clock, ShieldCheck, Mail,
  AlertTriangle, Eye, EyeOff, Check, QrCode,
} from 'lucide-react';
import api from '../api/axios';
import useAuthStore from '../store/useAuthStore';
import { MARCA_GENERICA } from '../utils/marca';
import './LoginNuevo.css';
import './ajustes-portadas.css';

/**
 * Pantalla de acceso.
 *
 * Mismo diseño que la de MediClinic Pro —foto a la izquierda, formulario a la
 * derecha, y en teléfono solo el formulario— con el contenido de este sistema.
 *
 * Aquí NO se nombra ninguna residencial: en este momento el sistema todavía no
 * sabe quién va a entrar, y poner el nombre de una sería mentirle al resto. La
 * marca de cada residencial aparece en cuanto la sesión se abre.
 */

/** Destino de cada rol al entrar. El superadministrador va a la plataforma. */
const DESTINOS = {
  resident: '/resident',
  guard: '/guard',
  admin: '/admin',
  superadmin: '/residenciales',
};

function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuthStore();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [verClave, setVerClave] = useState(false);
  // Bloq Mayús es la causa más común de «mi contraseña no funciona».
  const [mayusculas, setMayusculas] = useState(false);
  // Residencial desactivada: merece su propio aviso.
  const [inactiva, setInactiva] = useState(false);

  if (isAuthenticated && user) {
    return <Navigate to={DESTINOS[user.role] || '/'} replace />;
  }

  const cambiar = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    setInactiva(false);

    if (!formData.email || !formData.password) {
      setError('Escriba su correo y su contraseña.');
      return;
    }

    setCargando(true);
    try {
      const res = await api.post('/auth/login', formData);
      login(res.data.user, res.data.token);
      navigate(DESTINOS[res.data.user.role] || '/');
    } catch (err) {
      const datos = err.response?.data;

      if (!err.response) {
        setError('No se pudo conectar con el servidor. Revise su conexión.');
      } else if (datos?.code === 'RESIDENCIAL_INACTIVA') {
        // No es culpa de quien escribe: repetir «credenciales inválidas» lo
        // dejaría probando su contraseña a ciegas.
        setInactiva(true);
      } else if (datos?.code === 'USER_INACTIVE') {
        setError('Su cuenta está desactivada. Contacte a la administración de su residencial.');
      } else if (err.response.status === 401) {
        setError('Correo o contraseña incorrectos.');
      } else {
        setError(datos?.message || 'No se pudo iniciar sesión. Intente de nuevo.');
      }
    } finally {
      setCargando(false);
    }
  };

  return (
    /* `container-login` y no `page-login`: los estilos —y las variables de
       color que usan— están definidos sobre ESA clase. Con el nombre
       equivocado, la pantalla se dibujaba sin maquetación ni colores. */
    <div className="container-login">
      {/* Panel izquierdo — imagen. En teléfono no se muestra. */}
      <div className="image-panel-login">
        <img
          /* El panel es casi cuadrado y la tarjeta de vidrio le tapa el centro:
             el punto de enfoque va POR DEBAJO de la caseta (fp-y alto) para que
             ella suba en el encuadre y quede a la vista, sobre la tarjeta. */
          src="https://images.unsplash.com/photo-1775112077888-8fa36e9bbc51?auto=format&q=80&w=1100&h=1100&fit=crop&crop=focalpoint&fp-x=0.64&fp-y=0.72&fp-z=1.4"
          alt=""
          fetchPriority="high"
          className="image-bg-login"
        />

        <a
          href="/"
          className="image-brand-login"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
        >
          <ShieldCheck strokeWidth={2.25} aria-hidden="true" />
          <span>{MARCA_GENERICA.name}</span>
        </a>

        <div className="image-chip-login" aria-hidden="true">
          <span className="image-chip-text-login">
            <strong>Le suena el teléfono</strong>
            <span>En cuanto su visita llega a la caseta</span>
          </span>
          <span className="image-chip-check-login"><Check strokeWidth={3} /></span>
        </div>

        <div className="image-content-login">
          <h2 className="image-title-login">
            Quién entra a su residencial, siempre registrado.
          </h2>
          <div className="image-intro-login">
            <span className="image-label-login">{MARCA_GENERICA.name}</span>
            <p>
              El cuaderno de la caseta, en el sistema: quién llegó, a qué casa y a qué hora
              entró y salió.
            </p>
          </div>
        </div>

        <ul className="image-features-login">
          <li className="image-feature-login">
            <QrCode aria-hidden="true" />
            <span>Registro de visitas</span>
          </li>
          <li className="image-feature-login">
            <Clock aria-hidden="true" />
            <span>Aviso al instante</span>
          </li>
          <li className="image-feature-login">
            <ShieldCheck aria-hidden="true" />
            <span>Información aislada</span>
          </li>
        </ul>
      </div>

      {/* Panel derecho — formulario */}
      <div className="form-panel-login">
        {/* La salida va arriba, donde se busca; abajo del botón competiría con
            la acción principal. */}
        <div className="top-bar-login">
          <button type="button" className="back-home-link-login" onClick={() => navigate('/')}>
            <ArrowLeft /> Volver al inicio
          </button>
        </div>

        <div className="card-login">
          {/* En escritorio la marca ya la lleva el panel de la foto; aquí solo
              se ve en teléfono, donde ese panel desaparece. */}
          <div className="brand-login">
            <ShieldCheck className="brand-mark-login" strokeWidth={2.25} aria-hidden="true" />
            <span className="brand-name-login">{MARCA_GENERICA.name}</span>
          </div>

          <div className="header-login">
            <p className="eyebrow-login">Acceso al sistema</p>
            <h1 className="title-login">Iniciar sesión</h1>
            <p className="subtitle-login">Residentes, guardias y administración</p>
          </div>

          {inactiva && (
            <div className="suspended-banner-login">
              <AlertTriangle className="suspended-icon-login" />
              <div className="suspended-content-login">
                <h3 className="suspended-title-login">Acceso suspendido</h3>
                <p className="suspended-message-login">
                  La residencial no está activa en este momento. Su junta directiva o ConsolTech
                  pueden reactivarla.
                </p>
                <a
                  href="https://wa.me/50368960231?text=Hola%2C%20el%20acceso%20de%20mi%20residencial%20est%C3%A1%20suspendido."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="suspended-whatsapp-login"
                >
                  Contactar a ConsolTech
                </a>
              </div>
            </div>
          )}

          <form className="form-login" onSubmit={enviar}>
            {error && (
              <div className="error-login" role="alert">
                <span>{error}</span>
              </div>
            )}

            <div className="field-login">
              <label className="label-login" htmlFor="email">Correo electrónico</label>
              <div className="input-wrapper-login">
                <Mail className="input-icon-login" />
                <input
                  className="input-login"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={cambiar}
                  placeholder="correo@ejemplo.com"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="field-login">
              <label className="label-login" htmlFor="password">Contraseña</label>
              <div className="input-wrapper-login">
                <Lock className="input-icon-login" />
                <input
                  className="input-login input-with-toggle-login"
                  type={verClave ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={cambiar}
                  onKeyUp={(e) => setMayusculas(e.getModifierState?.('CapsLock') ?? false)}
                  onKeyDown={(e) => setMayusculas(e.getModifierState?.('CapsLock') ?? false)}
                  onBlur={() => setMayusculas(false)}
                  placeholder="Su contraseña"
                  autoComplete="current-password"
                  aria-describedby={mayusculas ? 'mayusculas-login' : undefined}
                />
                {/* Sin poder verla, un error de tecleo obliga a borrar todo a
                    ciegas — y en la caseta se escribe con prisa. */}
                <button
                  type="button"
                  className="toggle-password-login"
                  onClick={() => setVerClave((v) => !v)}
                  aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={verClave}
                  aria-controls="password"
                >
                  {verClave ? <EyeOff /> : <Eye />}
                </button>
              </div>
              {mayusculas && (
                <p id="mayusculas-login" className="caps-lock-login" aria-live="polite">
                  <AlertTriangle /> Bloq Mayús está activado
                </p>
              )}
            </div>

            <button className="button-login" type="submit" disabled={cargando} aria-busy={cargando}>
              <span>{cargando ? 'Ingresando…' : 'Ingresar'}</span>
              {!cargando && <ArrowRight className="button-icon-login" />}
            </button>
          </form>

          <p className="register-login">
            ¿Su residencial aún no está registrada?{' '}
            <button type="button" className="register-link-login" onClick={() => navigate('/adquirir')}>
              Regístrela aquí
            </button>
          </p>

          <p className="register-login">
            <CheckCircle2
              aria-hidden="true"
              style={{ width: 14, height: 14, verticalAlign: '-2px' }}
            />{' '}
            ¿Es residente y no tiene cuenta? Pídasela a la administración de su residencial.
          </p>
        </div>

        <p className="footer-login">
          Desarrollado por{' '}
          <a href="https://consoltechsv.com/" target="_blank" rel="noopener noreferrer">
            ConsolTech
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;
